import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  ClearOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  PoweroffOutlined,
  SearchOutlined,
  SettingOutlined,
  TeamOutlined
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import { classAPI, schoolAPI } from '../../services/api';
import '../../assets/css/ClassList.css';
import { useWorkbenchContext } from '../../contexts/WorkbenchContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const DEFAULT_PAGE_SIZE = 20;

const ClassList = () => {
  const workbench = useWorkbenchContext();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [currentClassId, setCurrentClassId] = useState(null);
  const [customToken, setCustomToken] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    grade: '',
    schoolId: workbench?.schoolId || null,
    status: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  const fetchSchools = async () => {
    try {
      const response = await schoolAPI.getAll({ pageSize: 200 });
      if (response.data.success) {
        setSchools(workbench ? response.data.data.filter(s => String(s.id) === workbench.schoolId) : response.data.data || []);
      }
    } catch (err) {
      console.error('获取学校列表失败:', err);
    }
  };

  const fetchClasses = async (page = pagination.current, pageSize = pagination.pageSize, currentFilters = filters) => {
    try {
      setLoading(true);
      setSearchLoading(true);
      setError(null);
      const response = await classAPI.getAll({
        page,
        pageSize,
        search: currentFilters.name || undefined,
        grade: currentFilters.grade || undefined,
        schoolId: workbench?.schoolId || currentFilters.schoolId || undefined,
        status: currentFilters.status || undefined
      });

      if (response.data.success) {
        const serverPagination = response.data.pagination || {};
        setClasses(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          current: Number(serverPagination.page ?? page),
          pageSize: Number(serverPagination.pageSize ?? pageSize),
          total: Number(serverPagination.total ?? 0)
        }));
      } else {
        setError(response.data.message || '获取班级列表失败');
      }
    } catch (err) {
      console.error('获取班级列表失败:', err);
      setError('获取班级列表失败，请检查网络连接或重新登录');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await classAPI.delete(id);
      if (response.data.success) {
        message.success('班级删除成功');
        fetchClasses();
      } else {
        message.error(response.data.message || '删除班级失败');
      }
    } catch (err) {
      console.error('删除班级失败:', err);
      message.error('删除班级失败');
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      const isCurrentlyActive = currentStatus !== 'disabled';
      const newStatus = isCurrentlyActive ? 'disabled' : 'active';
      const response = await classAPI.toggleStatus(id, newStatus);

      if (response.data.success) {
        message.success(`班级${newStatus === 'active' ? '启用' : '停用'}成功`);
        fetchClasses();
      } else {
        message.error(response.data.message || `班级${newStatus === 'active' ? '启用' : '停用'}失败`);
      }
    } catch (err) {
      console.error('更新班级状态失败:', err);
      message.error('操作失败，请稍后再试');
    }
  };

  const handleResetToken = async (id) => {
    try {
      const response = await classAPI.resetToken(id);
      if (response.data.success) {
        message.success('班级口令重置成功');
        setTokenModalVisible(false);
        fetchClasses();
      } else {
        message.error(response.data.message || '重置口令失败');
      }
    } catch (err) {
      console.error('重置口令失败:', err);
      message.error('重置口令失败');
    }
  };

  const showTokenModal = (id) => {
    setCurrentClassId(id);
    setCustomToken('');
    setTokenError('');
    setTokenModalVisible(true);
  };

  const handleSetCustomToken = async () => {
    if (customToken.length < 3) {
      setTokenError('口令长度必须大于等于 3 位');
      return;
    }

    try {
      setTokenLoading(true);
      const response = await classAPI.setToken(currentClassId, customToken);
      if (response.data.success) {
        message.success('班级口令设置成功');
        setTokenModalVisible(false);
        fetchClasses();
      } else {
        setTokenError(response.data.message || '设置口令失败');
      }
    } catch (err) {
      console.error('设置口令失败:', err);
      setTokenError('设置口令失败，请稍后再试');
    } finally {
      setTokenLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) {
      message.warning('当前班级没有口令');
      return;
    }

    navigator.clipboard.writeText(text).then(
      () => {
        message.success('口令已复制到剪贴板');
      },
      () => {
        message.error('复制失败，请手动复制');
      }
    );
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = (currentFilters = filters) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchClasses(1, pagination.pageSize, currentFilters);
  };

  const handleQuickSearch = (value) => {
    const newFilters = { ...filters, name: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const resetFilters = () => {
    const emptyFilters = {
      name: '',
      grade: '',
      schoolId: workbench?.schoolId || null,
      status: null
    };
    setFilters(emptyFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchClasses(1, pagination.pageSize, emptyFilters);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 90
    },
    {
      title: '班级名称',
      dataIndex: 'name',
      key: 'name',
      width: 160
    },
    {
      title: '年级',
      dataIndex: 'grade',
      key: 'grade',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '所属学校',
      dataIndex: 'school_name',
      key: 'school_name',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '班级口令',
      dataIndex: 'token',
      key: 'token',
      width: 160,
      render: (token, record) => {
        const value = token || record.class_code;
        return (
          <Space>
            <Text copyable={!!value}>{value || '-'}</Text>
            {value && (
              <Button
                type="link"
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copyToClipboard(value)}
              />
            )}
          </Space>
        );
      }
    },
    {
      title: '使用权限',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const isNormal = status === 'active' || status === 'online' || status === 'offline';
        return <Tag color={isNormal ? 'green' : 'red'}>{isNormal ? '正常' : '已停用'}</Tag>;
      }
    },
    {
      title: '在线状态',
      dataIndex: 'status',
      key: 'online_status',
      width: 110,
      render: (status) => (
        <Tag color={status === 'online' ? 'green' : 'default'}>
          {status === 'online' ? '在线' : '离线'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => (text ? new Date(text).toLocaleString() : '-')
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 520,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            icon={<SettingOutlined />}
            size="small"
            onClick={() => navigate(`/classes/manage/${record.id}`)}
          >
            管理
          </Button>

          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/classes/edit/${record.id}`)}
          >
            编辑
          </Button>

          <Button
            danger={record.status !== 'disabled'}
            type={record.status === 'disabled' ? 'primary' : 'default'}
            icon={<PoweroffOutlined />}
            size="small"
            onClick={() => handleStatusToggle(record.id, record.status)}
          >
            {record.status !== 'disabled' ? '停用' : '启用'}
          </Button>

          <Button
            type="primary"
            icon={<KeyOutlined />}
            size="small"
            onClick={() => showTokenModal(record.id)}
          >
            设置口令
          </Button>

          <Button
            icon={<BellOutlined />}
            size="small"
            onClick={() => navigate(`/classes/push-settings/${record.id}`)}
          >
            推送设置
          </Button>

          <Popconfirm
            title="确定要删除这个班级吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small">
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="class-list-container">
      <Card className="class-list-card" bordered={false}>
        <div className="class-list-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={2} className="class-list-title">
                <TeamOutlined style={{ marginRight: '12px' }} />
                班级管理
              </Title>
              <p className="class-list-subtitle">管理学校班级信息，设置班级口令和权限</p>
            </div>
            <Button
              className="add-class-btn"
              icon={<PlusOutlined />}
              onClick={() => navigate('/classes/create')}
              size="large"
            >
              添加班级
            </Button>
          </div>
        </div>

        <div className="class-list-actions">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div className="class-filter-section">
              <Row gutter={[16, 16]} className="class-filter-row">
                <Col xs={24} sm={12} md={6}>
                  <div className="class-filter-label">班级名称</div>
                  <Search
                    placeholder="搜索班级名称"
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    onSearch={handleQuickSearch}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div className="class-filter-label">年级</div>
                  <Input
                    placeholder="搜索年级"
                    value={filters.grade}
                    onChange={(e) => handleFilterChange('grade', e.target.value)}
                    onPressEnter={() => applyFilters()}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div className="class-filter-label">所属学校</div>
                  <Select
                    placeholder="选择学校"
                    value={filters.schoolId}
                    onChange={(value) => handleFilterChange('schoolId', value)}
                    allowClear
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {schools.map((school) => (
                      <Option key={school.id} value={school.id}>{school.name}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div className="class-filter-label">使用权限</div>
                  <Select
                    placeholder="选择状态"
                    value={filters.status}
                    onChange={(value) => handleFilterChange('status', value)}
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Option value="active">正常</Option>
                    <Option value="disabled">已停用</Option>
                  </Select>
                </Col>
              </Row>
              <Row className="class-filter-row">
                <Col span={24}>
                  <div className="filter-btn-group">
                    <Button className="filter-btn" icon={<ClearOutlined />} onClick={resetFilters}>
                      重置
                    </Button>
                    <Button
                      type="primary"
                      className="filter-btn filter-btn-primary"
                      icon={<SearchOutlined />}
                      onClick={() => applyFilters()}
                      loading={searchLoading}
                    >
                      筛选
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Space>
        </div>

        <div className="class-table-container">
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

          <Spin spinning={loading}>
            <Table
              className="class-table"
              columns={columns}
              dataSource={classes}
              rowKey="id"
              scroll={{ x: 1300 }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: [10, 20, 50, 100, 200],
                showTotal: (total) => `共 ${total} 条记录`
              }}
              onChange={(nextPagination) => {
                setPagination((prev) => ({
                  ...prev,
                  current: nextPagination.current,
                  pageSize: nextPagination.pageSize
                }));
              }}
            />
          </Spin>
        </div>
      </Card>

      <Modal
        className="class-modal"
        title="设置班级口令"
        open={tokenModalVisible}
        onCancel={() => setTokenModalVisible(false)}
        footer={[
          <Button key="reset" onClick={() => handleResetToken(currentClassId)} className="filter-btn">
            自动生成口令
          </Button>,
          <Button key="cancel" onClick={() => setTokenModalVisible(false)} className="filter-btn">
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={tokenLoading}
            onClick={handleSetCustomToken}
            className="filter-btn filter-btn-primary"
          >
            确定
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
            请输入新的班级口令，至少 3 位字符。
          </p>
          <Input
            value={customToken}
            onChange={(e) => setCustomToken(e.target.value)}
            placeholder="请输入新的班级口令"
            size="large"
            style={{ borderRadius: '8px' }}
          />
          {tokenError && (
            <Alert
              message={tokenError}
              type="error"
              showIcon
              style={{ marginTop: 12, borderRadius: '8px' }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ClassList;
